import React from 'react';
import { Button, Typography } from 'antd';
import { CheckOutlined, FireOutlined, SoundOutlined } from '@ant-design/icons';
import {
  KdsOrderWithTime,
  formatElapsedTime,
  getTimeColor,
} from './kdsHelpers';
import {
  KdsOrderAction,
  KdsOrderItems,
  getOrderFlags,
  passSecondsOf,
  resolveOrderAction,
} from './kdsOrderParts';

const { Text } = Typography;

interface KitchenOrderCardProps {
  order: KdsOrderWithTime;
  isMobile: boolean;
  soundEnabled: boolean;
  /** Whether the alarm was already acknowledged for this order (stops the pulse). */
  isAccepted: boolean;
  onAccept: (id: string) => void;
  onMarkReady: (id: string) => void;
  onMarkCompleted: (id: string) => void;
  onDispatchCourse: (orderId: string, itemIds: string[], courseName: string) => void;
  accepting: boolean;
  markingReady: boolean;
  completing: boolean;
}

const renderAction = (action: KdsOrderAction, isMobile: boolean) => {
  if (!action) return null;
  if (action.kind === 'doneBy') {
    return <span className="kds-card__done-by">{action.text}</span>;
  }
  if (action.kind === 'text') {
    return (
      <Text type="secondary" style={{ textAlign: 'center', flex: 1 }}>
        {action.text}
      </Text>
    );
  }
  const size = isMobile ? 'middle' : 'large';
  const icon = action.icon === 'sound' ? <SoundOutlined /> : <CheckOutlined />;
  if (action.tone === 'danger') {
    return (
      <Button icon={icon} onClick={action.run} loading={action.loading} size={size} danger>
        {action.label}
      </Button>
    );
  }
  const style = action.tone === 'alarm'
    ? { background: 'var(--kds-warn)', borderColor: 'var(--kds-warn)', color: '#181203' }
    : action.tone === 'ready'
      ? { background: 'var(--kds-ok)', borderColor: 'var(--kds-ok)', color: '#0d1a08' }
      : undefined;
  return (
    <Button type="primary" icon={icon} onClick={action.run} loading={action.loading} size={size} style={style}>
      {action.label}
    </Button>
  );
};

const KitchenOrderCard: React.FC<KitchenOrderCardProps> = ({
  order,
  isMobile,
  soundEnabled,
  isAccepted,
  onAccept,
  onMarkReady,
  onMarkCompleted,
  onDispatchCourse,
  accepting,
  markingReady,
  completing,
}) => {
  const { isPending, isReady, isCompleted, isLate } = getOrderFlags(order);
  const isWaitingForAcceptance = isPending && soundEnabled && !isAccepted;

  const action = resolveOrderAction(order, {
    soundEnabled,
    onAccept,
    onMarkReady,
    onMarkCompleted,
    onDispatchCourse,
    accepting,
    markingReady,
    completing,
  });

  const cardStateClass = isCompleted
    ? 'kds-card--done'
    : isLate
      ? 'kds-card--late'
      : isReady
        ? 'kds-card--ready'
        : isPending
          ? 'kds-card--new'
          : 'kds-card--prep';

  const renderTimer = () => {
    if (isReady) {
      const passSeconds = passSecondsOf(order);
      return (
        <div className="kds-timer kds-timer--ready">
          {passSeconds !== null ? `LISTO · ${formatElapsedTime(passSeconds)}` : '✓ LISTO'}
          {passSeconds !== null && <span className="kds-timer__sub">en el pase</span>}
        </div>
      );
    }
    if (isLate) {
      return (
        <div className="kds-timer kds-timer--late">
          {formatElapsedTime(order.elapsedSeconds)}
          <span className="kds-timer__sub">¿olvidada?</span>
        </div>
      );
    }
    return (
      <div className="kds-timer" style={{ color: getTimeColor(order.elapsedSeconds) }}>
        {formatElapsedTime(order.elapsedSeconds)}
      </div>
    );
  };

  return (
    <div className={`kds-card ${cardStateClass}${isWaitingForAcceptance ? ' kds-card--ringing' : ''}`}>
      <div className="kds-card__head">
        <div className="kds-card__meta">
          <b>{order.areaName} · {order.tableNumber}</b>
          {order.orderNumber ? (
            <>
              <span className="kds-sep">·</span>#{order.orderNumber}
            </>
          ) : null}
          {order.priority === 1 && (
            <FireOutlined style={{ color: 'var(--kds-bad)', fontSize: 15, marginLeft: 6 }} />
          )}
          <br />
          {order.waiterName}
        </div>
        {renderTimer()}
      </div>

      <div className="kds-card__items">
        <KdsOrderItems order={order} />
      </div>

      {order.kitchenNotes && (
        <div className="kds-card__notes">
          <strong>Notas cocina:</strong> {order.kitchenNotes}
        </div>
      )}

      <div className="kds-card__actions">
        {renderAction(action, isMobile)}
      </div>
    </div>
  );
};

export default KitchenOrderCard;
